<script setup lang="ts">
/*
 * Left flat sheet for phones and tablets (opened by the header hamburger via
 * `ui.mobileNavOpen`): browse + pre-orders, platforms (mark and live count,
 * "Coming soon" when the catalog has none), price buckets, account, help and
 * language. Mirrors the other menus; owns no data of its own.
 */
import { computed } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { CalendarClock, CircleHelp, LayoutGrid, LogOut, X } from 'lucide-vue-next';
import SideDrawer from './SideDrawer.vue';
import LanguageMenu from './LanguageMenu.vue';
import { ACCOUNT_LINKS, ADMIN_LINK } from './accountLinks';
import { PLATFORM_LINKS, PREORDER_LINK, PRICE_BUCKETS, platformCount, platformLink, priceBucketLink, type PlatformLink } from './navData';
import { usePlatformFacets } from './usePlatformFacets';
import { PLATFORM_ICON_PATHS } from '../../data/platformIcons';
import { useAuthStore } from '../../stores/auth';
import { useUiStore } from '../../stores/ui';
import logoUrl from '../../assets/logo.svg';

const { t } = useI18n();
const auth = useAuthStore();
const ui = useUiStore();
const router = useRouter();
const facets = usePlatformFacets();

const accountLinks = computed(() => (auth.isStaff ? [...ACCOUNT_LINKS, ADMIN_LINK] : ACCOUNT_LINKS));

/** Live product count per platform; null until the facets arrive. */
function countFor(p: PlatformLink): number | null {
  return platformCount(p, facets.value);
}

const TILE = 'flex items-center gap-3 rounded-lg bg-bg-elev-2 px-3 text-text-base transition-colors hover:bg-bg-mute';
const ROW = 'flex h-11 items-center gap-3 rounded-lg px-3 text-[15px] text-text-base transition-colors hover:bg-bg-elev-2';
const HEADING = 'text-eyebrow mb-2 mt-6 px-1';

// A link to the page you are already on never changes the route, so App's
// close-on-navigate would not fire; close on any link click instead.
function onNavClick(e: MouseEvent) {
  if ((e.target as HTMLElement).closest('a')) ui.closeMobileNav();
}

async function logout() {
  ui.closeMobileNav();
  await auth.logout();
  void router.push('/');
}
</script>

<template>
  <SideDrawer class="lg:hidden" side="left" width-class="w-[320px]" :open="ui.mobileNavOpen" :label="t('header.aria.menu')" @close="ui.closeMobileNav()">
    <template #default="{ close }">
      <div class="flex h-16 shrink-0 items-center justify-between border-b border-border pl-4 pr-3">
        <img :src="logoUrl" alt="KeyzoStore" width="178" height="31" class="h-[26px] w-auto" />
        <button type="button" class="btn-icon" :aria-label="t('common.actions.close')" @click="close">
          <X class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <nav class="flex-1 overflow-y-auto px-4 pb-8 pt-4" :aria-label="t('header.aria.menu')" @click="onNavClick">
        <div class="grid grid-cols-2 gap-2">
          <RouterLink to="/catalog" class="flex h-[72px] flex-col justify-center gap-1 rounded-lg bg-accent px-3 font-heading text-[12px] font-bold text-text-inv">
            <LayoutGrid class="h-5 w-5" aria-hidden="true" />{{ t('header.nav.allGames') }}
          </RouterLink>
          <RouterLink :to="PREORDER_LINK" class="flex h-[72px] flex-col justify-center gap-1 rounded-lg bg-bg-mute px-3 font-heading text-[12px] font-bold text-text-base">
            <CalendarClock class="h-5 w-5 text-accent" aria-hidden="true" />{{ t('header.nav.preorders') }}
          </RouterLink>
        </div>

        <p :class="HEADING">{{ t('header.menu.platforms') }}</p>
        <ul class="grid grid-cols-2 gap-2">
          <li v-for="p in PLATFORM_LINKS" :key="p.icon">
            <component
              :is="countFor(p) === 0 ? 'div' : RouterLink"
              :to="countFor(p) === 0 ? undefined : platformLink(p, facets)"
              :class="[TILE, 'h-14', countFor(p) === 0 ? 'opacity-50' : '']"
            >
              <svg viewBox="0 0 24 24" class="h-5 w-5 shrink-0 text-text-dim" fill="currentColor" aria-hidden="true"><path :d="PLATFORM_ICON_PATHS[p.icon]" /></svg>
              <span class="min-w-0">
                <span class="block truncate font-heading text-[12px] font-bold leading-tight">{{ p.name }}</span>
                <span class="block text-[11px] leading-tight text-text-faint tabular-nums">
                  <template v-if="countFor(p) === 0">{{ t('header.menu.comingSoon') }}</template>
                  <template v-else-if="countFor(p) !== null">{{ countFor(p)?.toLocaleString('en-US') }}</template>
                </span>
              </span>
            </component>
          </li>
        </ul>

        <p :class="HEADING">{{ t('header.menu.shopByPrice') }}</p>
        <ul class="grid grid-cols-2 gap-2">
          <li v-for="b in PRICE_BUCKETS" :key="b.key">
            <RouterLink :to="priceBucketLink(b)" :class="[TILE, 'h-11 justify-center font-heading text-[12px] font-bold']">{{ t(`header.price.${b.key}`) }}</RouterLink>
          </li>
        </ul>

        <p :class="HEADING">{{ t('header.account') }}</p>
        <template v-if="auth.isAuthenticated">
          <RouterLink v-for="l in accountLinks" :key="l.to" :to="l.to" :class="[ROW, l === ADMIN_LINK ? '!text-accent' : '']">
            <component :is="l.icon" class="h-[18px] w-[18px]" aria-hidden="true" />{{ t(`header.menu.${l.label}`) }}
          </RouterLink>
        </template>
        <div v-else class="grid grid-cols-2 gap-2">
          <RouterLink to="/login" class="btn-secondary btn-sm">{{ t('header.login') }}</RouterLink>
          <RouterLink to="/register" class="btn-secondary btn-sm">{{ t('header.signup') }}</RouterLink>
        </div>

        <RouterLink to="/help" :class="[ROW, 'mt-2']"><CircleHelp class="h-[18px] w-[18px]" aria-hidden="true" />{{ t('header.nav.help') }}</RouterLink>

        <div class="flex items-center justify-between px-1 pt-5">
          <span class="text-[13px] text-text-dim">{{ t('header.menu.language') }}</span>
          <LanguageMenu />
        </div>

        <button v-if="auth.isAuthenticated" type="button" :class="[ROW, 'mt-4 w-full !text-error hover:!bg-error/10']" @click="logout">
          <LogOut class="h-[18px] w-[18px]" aria-hidden="true" />{{ t('common.actions.signOut') }}
        </button>
      </nav>
    </template>
  </SideDrawer>
</template>
